// frontend/src/components/chat/ChatInput.jsx - Enhanced with smart commands
import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { Send, Paperclip, Mic, Square, Zap, Search, MousePointer, FormInput } from 'lucide-react'

const ChatInput = ({ 
  onSendMessage, 
  placeholder = "Type your message...", 
  disabled = false,
  maxLength = 1000,
  sessionActive = false 
}) => {
  const [message, setMessage] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [showQuickActions, setShowQuickActions] = useState(true)
  const textareaRef = useRef(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (message.trim() && !disabled) {
      onSendMessage(message.trim())
      setMessage('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleInputChange = (e) => {
    setMessage(e.target.value)
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px'
    }
  }

  const toggleRecording = () => {
    setIsRecording(!isRecording)
    // Implement voice recording logic here
  }

  const handleFileUpload = () => {
    // Implement file upload logic here
  }

  const handleQuickAction = (action) => {
    setMessage(action)
    onSendMessage(action)
  }

  // Enhanced quick actions based on session state
  const getQuickActions = () => {
    if (!sessionActive) {
      return [
        { text: 'Start browsing session', icon: <Zap size={14} />, category: 'session' },
        { text: 'Go to google.com', icon: <Search size={14} />, category: 'navigation' },
        { text: 'Visit news website', icon: <Search size={14} />, category: 'navigation' },
        { text: 'Browse e-commerce site', icon: <Search size={14} />, category: 'navigation' }
      ]
    }

    return [
      // Smart clicking actions
      { text: 'Click that link', icon: <MousePointer size={14} />, category: 'interaction' },
      
      // Forms actions
      { text: 'Find contact form', icon: <FormInput size={14} />, category: 'forms' },
      { text: 'Find login form', icon: <FormInput size={14} />, category: 'forms' },
      { text: 'Show all forms', icon: <FormInput size={14} />, category: 'forms' },
      
      // Page analysis
      { text: 'Analyze this page', icon: <Search size={14} />, category: 'analysis' },
      { text: 'Find all links', icon: <Search size={14} />, category: 'analysis' },
      { text: 'Extract contact info', icon: <Search size={14} />, category: 'analysis' },
      
      // Navigation
      { text: 'Go back', icon: <MousePointer size={14} />, category: 'navigation' },
      { text: 'Scroll down', icon: <MousePointer size={14} />, category: 'navigation' },
      
      // Tools
      { text: 'Take screenshot', icon: <Search size={14} />, category: 'utility' }
    ]
  }

  const quickActions = getQuickActions()
  
  // Group actions by category
  const groupedActions = quickActions.reduce((groups, action) => {
    const category = action.category || 'general'
    if (!groups[category]) groups[category] = []
    groups[category].push(action)
    return groups
  }, {})

  const getCategoryColor = (category) => {
    const colors = {
      session: 'bg-green-100 text-green-700 hover:bg-green-200',
      navigation: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      interaction: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      forms: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
      analysis: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
      utility: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      general: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
    return colors[category] || colors.general
  }

  const getCategoryLabel = (category) => {
    const labels = {
      session: '🚀 Session',
      navigation: '🧭 Navigate', 
      interaction: '👆 Click',
      forms: '📝 Forms',
      analysis: '🔍 Analyze',
      utility: '🛠️ Tools',
      general: '⚡ Quick'
    }
    return labels[category] || labels.general
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative flex items-end space-x-2">
        {/* File upload button */}
        <button
          type="button"
          onClick={handleFileUpload}
          disabled={disabled}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <Paperclip className="h-5 w-5" />
        </button>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            rows={2}
            className="w-full px-4 py-4 pr-12 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-base min-h-[64px] max-h-[200px]"
            style={{ minHeight: '64px', maxHeight: '200px' }}
          />
          
          {/* Character count */}
          {message.length > maxLength * 0.8 && (
            <div className="absolute bottom-1 right-12 text-xs text-gray-500">
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Voice recording button */}
        <motion.button
          type="button"
          onClick={toggleRecording}
          disabled={disabled}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-50 ${
            isRecording 
              ? 'bg-danger-100 text-danger-600 hover:bg-danger-200' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          {isRecording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </motion.button>

        {/* Send button */}
        <motion.button
          type="submit"
          disabled={!message.trim() || disabled}
          whileHover={{ scale: disabled || !message.trim() ? 1 : 1.05 }}
          whileTap={{ scale: disabled || !message.trim() ? 1 : 0.95 }}
          className="flex-shrink-0 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="h-5 w-5" />
        </motion.button>
      </div>

      <div className="space-y-2">
        {/* Toggle quick actions */}
        {/* <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            {sessionActive ? '🎯 Smart Actions' : '🚀 Get Started'}
          </span>
          <button
            type="button"
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            {showQuickActions ? 'Hide' : 'Show'}
          </button>
        </div> */}

        {/* Action categories - Compact layout */}
        {showQuickActions && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {/* {Object.entries(groupedActions).map(([category, actions]) => (
              <div key={category} className="space-y-1">
                <div className="text-xs font-medium text-gray-500 px-1">
                  {getCategoryLabel(category)}
                </div>
                <div className="flex flex-wrap gap-1">
                  {actions.map((action, index) => (
                    <motion.button
                      key={`${category}-${index}`}
                      type="button"
                      onClick={() => handleQuickAction(action.text)}
                      disabled={disabled}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center space-x-1 px-2 py-1 text-xs rounded-md transition-colors disabled:opacity-50 ${getCategoryColor(category)}`}
                    >
                      {action.icon}
                      <span>{action.text}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))} */}

            {/* Smart command examples - Collapsible when session is active */}
            {/* {sessionActive && (
              <details className="mt-2">
                <summary className="text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-800">
                  💡 Try these smart commands
                </summary>
                <div className="mt-1 pl-4 space-y-0.5 text-xs text-gray-500">
                  <div>• "Click the contact us link"</div>
                  <div>• "Find the signup form and fill it"</div>
                  <div>• "Scroll down and click pricing"</div>
                  <div>• "Extract all email addresses"</div>
                  <div>• "Go to the about page and then contact"</div>
                </div>
              </details>
            )} */}
          </div>
        )}

        {/* Collapsed quick actions toggle */}
        {/* {!showQuickActions && (
          <button
            type="button"
            onClick={() => setShowQuickActions(true)}
            className="w-full py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            Show Quick Actions
          </button>
        )} */}
      </div>
    </form>
  )
}

export default ChatInput